import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { and, not } from 'ember-truth-helpers';
import GeneralTooltipsTooltipEvents from 'lumen/components/general/tooltips/tooltip-events';
import type { Period } from 'lumen/models';

interface MarketRatesTablesCellsThDateSignature {
  Args: {
    column: {
      isFirstColumn: boolean;
      isLoading: boolean;
      periodId: string;
    };
  };
}

export default class MarketRatesTablesCellsThDate extends Component<MarketRatesTablesCellsThDateSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service declare store: Services['store'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get events() {
    if (!this.period?.day?.calendarEvents?.length) {
      return [];
    }

    const events = [...this.period.day.calendarEvents];
    return events.sort((a, b) => b.timesAccepted - a.timesAccepted);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHolidaysOrEvents() {
    if (!this.access.hasFeature('app.market.events')) {
      return false;
    }

    return this.holidays.length > 0 || this.events.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get holidays() {
    if (!this.period?.day?.holidays?.length) {
      return [];
    }

    return [...this.period.day.holidays];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get label() {
    if (!this.period?.fromDate) {
      return null;
    }

    return this.period.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_MONTH_AND_DAY_SHORT,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get period() {
    return this.store.peekRecord<Period>('period', this.args.column.periodId);
  }

  <template>
    <div>
      {{#unless @column.isFirstColumn}}
        {{#if @column.isLoading}}
          <div class="flex h-full w-full flex-col justify-center px-200">
            <div class="table-loading-state-bar"></div>
          </div>
        {{else}}
          <div class="flex items-center justify-start gap-100">
            <p>{{this.label}}</p>
            {{#if (and this.hasHolidaysOrEvents (not @column.isFirstColumn))}}
              <PrismIcon @name="calendar-accepted" @size="small" />
              <EmberTooltip
                @side="right"
                @tooltipClass="base-tooltip day-detail-tooltip"
              >
                <GeneralTooltipsTooltipEvents
                  @events={{this.events}}
                  @holidays={{this.holidays}}
                  @title="menu.events"
                />
              </EmberTooltip>
            {{/if}}
          </div>
        {{/if}}
      {{/unless}}
    </div>
  </template>
}
